import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SectionTitle } from '@repo/components/containers';
import { fetchMoreSearchResults, selectSearch } from '@repo/redux/actions/search';
import { CircledCheckIcon, CircledXIcon, LoaderIcon } from '@repo/icons';
import styles from './Search.module.scss';
import { SearchState, ViewID } from '@repo/utils/types';
import ResultsDisplay from './ResultsDisplay';
import { useAppContactSelecting, useSaveViewState } from '@repo/hooks';
import { selectSelectedContact, selectSelectedContactBatch } from '@repo/redux/actions/ui';
import { useScrollRestoration } from '@components/utils/use-scroll-restoration';
import { NoResultsFound } from './EmptySearch';
import { selectNavBarState } from '@repo/redux/reducers/ui/navBar';

const SearchTitle = ({ search }: { search: SearchState }) => {
  const { status: navBarStatus } = useSelector(selectNavBarState);
  const { totalResults, isFetching } = search.results;
  const forTerm = ` for “${search.term}”`;
  const forSuffix = navBarStatus === 'pinned' ? '' : forTerm;
  let text = `${totalResults} Matches Found${forSuffix}`;
  let Icon = CircledCheckIcon;

  if (search.term.length === 0) {
    return <div className={styles.resultsHeaderContainer}>Search</div>;
  }

  if (isFetching) {
    if (search.results.page == 0) {
      text = `Searching ${forTerm}`;
    } else {
      text = `Loading more matches${forTerm}`;
    }

    Icon = ({ className }) => <LoaderIcon className={className} width={12} height={12} />;
  } else if (totalResults === 0) {
    text = `No Matches Found${forSuffix}`;

    Icon = CircledXIcon;
  } else if (totalResults === 1) {
    text = `1 Match Found${forSuffix}`;
  }

  return (
    <div className={styles.resultsHeaderContainer}>
      Search
      <SectionTitle className={styles.resultsHeader} IconComponent={Icon}>
        {text}
      </SectionTitle>
    </div>
  );
};

const viewID: ViewID = 'Search';

export default function Search() {
  const dispatch = useDispatch();
  const scrollingContainerRef = useRef();
  const search = useSelector(selectSearch);
  const selectedContact = useSelector(selectSelectedContact);
  const selectedContactBatch = useSelector(selectSelectedContactBatch);
  const [highlightedItem, setHighlightedItem] = useState(selectedContact);
  const defaultHighlightedItem = useMemo(() => highlightedItem, []);
  const loadNextPage = useCallback(() => dispatch(fetchMoreSearchResults()), []);
  useScrollRestoration({ viewID, scrollingContainerRef });

  useAppContactSelecting({
    contact: search.term.length > 0 && highlightedItem,
    source: 'search',
    additionalDeps: [search.term],
  });

  useEffect(() => {
    if (search.term.length === 0) {
      setHighlightedItem(null);
    }
  }, [search.term]);

  useSaveViewState(
    viewID,
    { selectedContact: highlightedItem, selectedContactBatch, searchTerm: search.term },
    [highlightedItem, selectedContactBatch, search.term],
  );

  return (
    <ResultsDisplay
      search={search}
      setHighlightedItem={setHighlightedItem}
      highlightedItem={selectedContact}
      defaultHighlightedItem={defaultHighlightedItem}
      loadNextPage={loadNextPage}
      title={<SearchTitle search={search} />}
      ref={scrollingContainerRef}
      resetListHighlightOnSearch
      emptyStateUI={<NoResultsFound />}
    />
  );
}
